import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react-lite';
import { LottieSource } from '@lower-financial/core-components/src/components/lottie-source/lottie-source';
import Scene from '../scene/scene';
import H3 from '../../modules/lower-ui-kit/components/typography/h3';
import H5 from '../../modules/lower-ui-kit/components/typography/h5';
import H6 from '../../modules/lower-ui-kit/components/typography/h6';
import FileUploadProvider from '../../modules/lower-ui-kit/components/file-upload/file-upload-provider';
import Button, { ButtonType } from '../../modules/lower-ui-kit/components/button/button';
import { CallToAction } from '../../types/call-to-action';
import useDocumentTitle from '../../modules/lower-ui-kit/hooks/useDocumentTitle/useDocumentTitle';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { NotificationType } from '../../hooks/useNotifications/UserNotification';
import spinner from '../../modules/lower-ui-kit/components/Spinner/loading-spinner.json';
import useUploadSceneModel from './upload-scene-model';

type UploadPageProps = {
  title: string;
  tabTitle: string;
  finishButton: CallToAction;
  pageTitle: string;
  helperTitle: string;
  helperCopy: string;
};

function UploadSceneView({
  title,
  tabTitle,
  finishButton,
  pageTitle,
  helperTitle,
  helperCopy,
}: UploadPageProps) {
  const { onLogOut, uploadTokens } = useUploadSceneModel();

  const titlePrefix = 'DocUpload | ';

  useDocumentTitle(`${titlePrefix}${pageTitle}`);

  const { notify, clearNotifications } = useNotifications();

  const uploadInfo = {
    message: helperCopy,
    notificationType: NotificationType.Info,
    title: helperTitle,
  };

  const maintenanceInfo = process.env.MAINTENANCE_INFO ?? '';

  useEffect(() => {
    clearNotifications();
    if (uploadTokens.length === 0) {
      notify(uploadInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Scene
      fullWidth
      background
    >
      <Container>
        <Header>
          <Wrapper>
            <Title>
              {title}
            </Title>
            <MaintenanceInfo>
              {maintenanceInfo}
            </MaintenanceInfo>
            <TabTitle>
              {tabTitle}
            </TabTitle>
          </Wrapper>
        </Header>
        <DropContainer>
          <FileUploadProvider
            renderChildren={
              (viewModel) => (
                <FinishButton
                  type={ButtonType.Form}
                  onClick={onLogOut}
                  disabled={viewModel.isUploading}
                >
                  {!viewModel.isUploading && finishButton.label}
                  {viewModel.isUploading && (
                    <span
                      style={{
                        alignItems: 'center',
                        display: 'flex',
                        height: '100%',
                        justifyContent: 'center',
                        left: 0,
                        position: 'absolute',
                        top: 0,
                        width: '100%',
                      }}
                    >
                      <span
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '64%',
                        }}
                      >
                        <LottieSource
                          lottieJSONFile={spinner}
                          style={{
                            height: '22px',
                            width: '22px',
                          }}
                        />
                        {finishButton.loadingLabel}
                      </span>

                    </span>
                  )}
                </FinishButton>
              )
            }
          />
        </DropContainer>
      </Container>
    </Scene>
  );
}

const FinishButton = styled(Button)`
  animation: pop-in 0.5s forwards;
  ${({ theme }) => css`
    @keyframes pop-in {
      0% {
        opacity: 0;
        transform: ${theme.finishButton.transformFrom};
      }
      100% {
        opacity: 1; 
        transform: ${theme.finishButton.transformTo};
      }
    }
    &:disabled {
      cursor: not-allowed;
    }
    height: ${theme.finishButton.height};
    position: ${theme.finishButton.position};
    margin-left: ${theme.finishButton.marginLeft};
    left: ${theme.finishButton.left};
    width: ${theme.finishButton.width};
    bottom: ${theme.finishButton.bottom};
    ${theme.media.tablet} {
      width: ${theme.finishButton.mobileWidth};
      border-radius: ${theme.finishButton.mobileBorderRadius};
      left: ${theme.finishButton.mobileLeft};
      margin: ${theme.finishButton.mobileMargin};
      bottom: ${theme.finishButton.mobileBottom};
    }
  `}
  z-index: 2;

`;

const Container = styled.div`
  margin: 0 auto;
  width: 100%;
  ${({ theme }) => `
    max-width: ${theme.uploadPage.containerMaxWidth};
     `
}
`;

const Wrapper = styled.div`
  padding-left: 40px;
  padding-right: 40px;
  margin: 0 auto;
`;

const Header = styled.div`
  ${({ theme }) => css`
    padding:  ${theme.uploadPage.headerPadding};
    background-color: ${theme.uploadPage.headerBackgroundColor};
    ${theme.media.tablet}
    {
      padding:  ${theme.uploadPage.mobileHeaderPadding};
    }
  `}
`;

const DropContainer = styled.div`
`;

const Title = styled(H3)`
  font-size: 60px;
  letter-spacing: -2px;
  ${({ theme }) => css`
    color: ${theme.uploadPage.titleColor};
    padding-bottom: ${theme.uploadPage.titleBottomPadding};
    padding-top: ${theme.uploadPage.titleTopPadding};
    ${theme.media.tablet}
    {
      font-size: 52px;
    }
  `}
`;

const TabTitle = styled(H6)`
  font-size: 15px;
  ${({ theme }) => css`
    color: ${theme.colors.currentSession};
    padding-bottom: ${theme.uploadPage.tabTitleBottomPadding};
  `}
`;

const MaintenanceInfo = styled(H5)`
  ${({ theme }) => css`
    font-size: ${theme.maintenanceInfo.fontSize};
    color: ${theme.maintenanceInfo.color};
    margin: ${theme.maintenanceInfo.margin};
  `}
`;

export default observer(UploadSceneView);
